import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Upload, X, Sparkles, CalendarDays } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { PROJECT_TYPES, SIZES, PLACEMENTS } from "../data/site";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const STEPS = ["Style", "Artist", "Details", "Reference", "Contact"];

export default function Booking() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });
  const { data: artists = [] } = useQuery({ queryKey: ["artists"], queryFn: async () => (await api.get("/artists")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  const styleMap = Object.fromEntries(styles.map((s) => [s.slug, s.name]));
  const artistParam = params.get("artist") || "";
  const lockedArtist = artistParam ? artists.find((a) => a.slug === artistParam) : null;

  const [form, setForm] = useState({
    style_slug: "", artist_slug: "", recommend_artist: false, project_type: "",
    placement: "", size: "", description: "", reference_images: [],
    preferred_date: "", preferred_time: "", name: "", email: "", phone: "",
  });

  useEffect(() => {
    const a = params.get("artist");
    const s = params.get("style");
    setForm((f) => ({ ...f, ...(a ? { artist_slug: a } : {}), ...(s ? { style_slug: s } : {}) }));
  }, [params]);

  const recommended = useMemo(() => {
    if (!form.style_slug) return artists;
    return artists.filter((a) => (a.styles || []).includes(form.style_slug));
  }, [artists, form.style_slug]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canNext = () => {
    if (step === 0) return !!form.style_slug;
    if (step === 1) return !!form.artist_slug || form.recommend_artist;
    if (step === 2) return true;
    return true;
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        set({ reference_images: [...form.reference_images, data.path] });
      }
      toast.success("Reference uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeRef = (path) => set({ reference_images: form.reference_images.filter((p) => p !== path) });

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, artist_slug: form.recommend_artist ? null : form.artist_slug || null };
      const { data } = await api.post("/bookings", payload);
      setDone(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (settings && settings.bookings_open === false) {
    return (
      <div className="pt-44 pb-32 text-center px-6 max-w-xl mx-auto">
        <p className="eyebrow mb-6">Booking</p>
        <h1 className="display text-5xl text-white mb-6">Books are closed.</h1>
        <p className="text-[#9E9EA8]">We're not taking new requests right now. Call the studio at (571) 378-1993 or check back soon.</p>
      </div>
    );
  }

  if (done) {
    return (
      <>
        <Seo title="Request Received" />
        <section className="min-h-[85vh] flex flex-col items-center justify-center text-center px-6 pt-24" data-testid="booking-confirmation">
          <div className="w-16 h-16 border border-ember flex items-center justify-center mb-8"><Check className="text-ember" size={28} /></div>
          <p className="eyebrow mb-6">Request #{done.id.slice(0, 8)}</p>
          <h1 className="display text-6xl lg:text-7xl text-white">Request received.</h1>
          <p className="mt-8 max-w-lg text-[#9E9EA8] text-lg leading-relaxed">
            Thank you, {form.name.split(" ")[0] || "friend"}. The studio will review your idea{form.recommend_artist ? " and match you with the right artist" : form.artist_slug ? ` and ${styleMap[form.style_slug] ? "" : ""}${artists.find((a) => a.slug === form.artist_slug)?.name || "your artist"} will be in touch` : ""}. Expect a reply at {form.email}.
        </p>
        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <Link to="/portfolio" className="border border-ash text-white font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-white transition-colors">Explore the Work</Link>
          <Link to="/" className="bg-ember text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 hover:bg-white transition-colors">Back Home</Link>
        </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo title="Book a Session" description="Request a custom tattoo at Obsidian Art & Tattoo Studio, Springfield VA. Choose a style, find your artist, and submit your idea." />

      <section className="pt-32 lg:pt-40 pb-8 max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
        <p className="eyebrow mb-5">Booking request</p>
        <h1 className="display text-5xl sm:text-6xl lg:text-7xl text-white">Book a session.</h1>

        {/* progress */}
        <div className="mt-10 flex items-center gap-2 sm:gap-3">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 sm:gap-3 flex-1">
              <button
                onClick={() => i < step && setStep(i)}
                data-testid={`booking-step-tab-${i}`}
                className={`flex items-center gap-2 shrink-0 ${i <= step ? "text-white" : "text-[#555562]"} ${i < step ? "cursor-pointer" : "cursor-default"}`}
              >
                <span className={`w-7 h-7 flex items-center justify-center border font-mono text-xs ${i < step ? "bg-ember border-ember text-obsidian" : i === step ? "border-ember text-ember" : "border-ash"}`}>
                  {i < step ? <Check size={13} /> : i + 1}
                </span>
                <span className="hidden sm:inline font-mono text-[0.65rem] uppercase tracking-widest">{s}</span>
              </button>
              {i < STEPS.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-ember" : "bg-ash"}`} />}
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16 pb-24">
        <div className="border border-ash p-6 sm:p-10 min-h-[380px]">
          {/* STEP 0 — STYLE */}
          {step === 0 && (
            <div data-testid="booking-step-style">
              <h2 className="font-serif text-3xl text-white mb-2">What are you thinking?</h2>
              <p className="text-[#9E9EA8] mb-8">
                {lockedArtist
                  ? <>Pick the style you'd like — this helps <span className="text-ember">{lockedArtist.name}</span> prep for your session.</>
                  : "Pick a style and we'll surface the artists who specialize in it."}
              </p>
              <div className="flex flex-wrap gap-3">
                {[...styles, { slug: "other", name: "Other / Not sure" }].map((s) => (
                  <button key={s.slug} data-testid={`booking-style-${s.slug}`} onClick={() => set({ style_slug: s.slug, ...(lockedArtist ? {} : { artist_slug: "" }) })}
                    className={`font-mono text-xs uppercase tracking-[0.15em] px-5 py-3 border transition-colors ${form.style_slug === s.slug ? "bg-ember text-obsidian border-ember" : "border-ash text-[#C4C4CC] hover:border-white hover:text-white"}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 1 — ARTIST */}
          {step === 1 && (
            <div data-testid="booking-step-artist">
              {lockedArtist ? (
                <>
                  <h2 className="font-serif text-3xl text-white mb-2">Your artist.</h2>
                  <p className="text-[#9E9EA8] mb-6">You're booking directly with <span className="text-ember">{lockedArtist.name}</span>.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div data-testid={`booking-artist-${lockedArtist.slug}`} className="text-left border border-ember overflow-hidden">
                      <div className="mono-hover aspect-square overflow-hidden"><img src={fileUrl(lockedArtist.photo)} alt={lockedArtist.name} className="w-full h-full object-cover" /></div>
                      <div className="p-3">
                        <span className="block text-white font-serif text-lg leading-none">{lockedArtist.name}</span>
                        <span className="block text-[#828290] font-mono text-[0.6rem] uppercase tracking-widest mt-1">{lockedArtist.experience}</span>
                      </div>
                    </div>
                  </div>
                  <Link to="/artists" data-testid="booking-change-artist" className="inline-block mt-6 font-mono text-[0.65rem] uppercase tracking-widest text-[#828290] hover:text-white transition-colors">Change artist</Link>
                </>
              ) : (
                <>
                  <h2 className="font-serif text-3xl text-white mb-2">Choose your artist.</h2>
                  {form.style_slug && (
                    <p className="text-[#9E9EA8] mb-6">Artists specializing in <span className="text-ember">{styleMap[form.style_slug]}</span>:</p>
                  )}
                  <button
                    data-testid="booking-recommend"
                    onClick={() => set({ recommend_artist: !form.recommend_artist, artist_slug: "" })}
                    className={`w-full flex items-center gap-4 p-5 border mb-6 transition-colors ${form.recommend_artist ? "border-ember bg-ember/10" : "border-ash hover:border-white"}`}>
                    <span className={`w-9 h-9 flex items-center justify-center border ${form.recommend_artist ? "border-ember text-ember" : "border-ash text-[#828290]"}`}><Sparkles size={16} /></span>
                    <span className="text-left">
                      <span className="block text-white font-serif text-xl">Recommend an artist for me</span>
                      <span className="block text-[#828290] text-sm">Let the studio match you based on your idea.</span>
                    </span>
                  </button>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {recommended.map((a) => (
                      <button key={a.slug} data-testid={`booking-artist-${a.slug}`}
                        onClick={() => set({ artist_slug: a.slug, recommend_artist: false })}
                        className={`text-left border overflow-hidden transition-colors ${form.artist_slug === a.slug ? "border-ember" : "border-ash hover:border-white"}`}>
                        <div className="mono-hover aspect-square overflow-hidden"><img src={fileUrl(a.photo)} alt={a.name} className="w-full h-full object-cover" /></div>
                        <div className="p-3">
                          <span className="block text-white font-serif text-lg leading-none">{a.name}</span>
                          <span className="block text-[#828290] font-mono text-[0.6rem] uppercase tracking-widest mt-1">{a.experience}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  {recommended.length === 0 && <p className="text-[#828290] text-sm">No exact specialist — choose "recommend" and we'll match you.</p>}
                </>
              )}
            </div>
          )}

          {/* STEP 2 — DETAILS */}
          {step === 2 && (
            <div data-testid="booking-step-details" className="space-y-6">
              <h2 className="font-serif text-3xl text-white">Tell us about it.</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Select label="Project type" value={form.project_type} onChange={(v) => set({ project_type: v })} options={PROJECT_TYPES} testid="booking-project" />
                <Select label="Placement" value={form.placement} onChange={(v) => set({ placement: v })} options={PLACEMENTS} testid="booking-placement" />
                <Select label="Approx. size" value={form.size} onChange={(v) => set({ size: v })} options={SIZES} testid="booking-size" />
              </div>
              <label className="block">
                <span className="eyebrow block mb-3">Describe your idea <span className="text-[#555562] normal-case tracking-normal">(optional)</span></span>
                <textarea data-testid="booking-description" rows={5} value={form.description} onChange={(e) => set({ description: e.target.value })}
                  placeholder="Subject, mood, references, anything meaningful to you…" className="obs-input resize-none" />
              </label>
            </div>
          )}

          {/* STEP 3 — REFERENCE */}
          {step === 3 && (
            <div data-testid="booking-step-reference">
              <h2 className="font-serif text-3xl text-white mb-2">Reference images.</h2>
              <p className="text-[#9E9EA8] mb-8">Optional but helpful — inspiration, placement photos, existing tattoos for cover-ups.</p>
              <label className="flex flex-col items-center justify-center gap-3 border border-dashed border-ash py-14 cursor-pointer hover:border-ember transition-colors" data-testid="booking-upload-label">
                <Upload size={28} className="text-[#828290]" />
                <span className="font-mono text-xs uppercase tracking-widest text-[#C4C4CC]">{uploading ? "Uploading…" : "Click to upload images"}</span>
                <span className="text-[#555562] text-xs">JPG, PNG, WEBP — up to 12MB each</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="booking-upload-input" />
              </label>
              {form.reference_images.length > 0 && (
                <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {form.reference_images.map((p) => (
                    <div key={p} className="relative aspect-square border border-ash overflow-hidden group">
                      <img src={fileUrl(p)} alt="reference" className="w-full h-full object-cover" />
                      <button onClick={() => removeRef(p)} className="absolute top-1 right-1 bg-obsidian/80 p-1 text-white hover:text-ember" data-testid="booking-remove-ref"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="block">
                  <span className="eyebrow block mb-3">Preferred date</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="booking-date" className="obs-input flex items-center justify-between text-left">
                        <span className={form.preferred_date ? "text-white" : "text-[#555562]"}>{form.preferred_date ? new Date(form.preferred_date + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "Select a date"}</span>
                        <CalendarDays size={16} className="text-[#828290]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0 bg-charcoal border-ash text-white">
                      <Calendar
                        mode="single"
                        selected={form.preferred_date ? new Date(form.preferred_date + "T00:00:00") : undefined}
                        onSelect={(d) => set({ preferred_date: d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "" })}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        classNames={{ day_selected: "bg-ember text-obsidian hover:bg-ember focus:bg-ember", day_today: "text-violet" }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Select label="Preferred time" value={form.preferred_time} onChange={(v) => set({ preferred_time: v })} options={["Morning", "Afternoon", "Evening", "Flexible"]} testid="booking-time" />
              </div>
            </div>
          )}

          {/* STEP 4 — CONTACT */}
          {step === 4 && (
            <div data-testid="booking-step-contact" className="space-y-6">
              <h2 className="font-serif text-3xl text-white">Almost there.</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <label className="block"><span className="eyebrow block mb-3">Full name *</span><input data-testid="booking-name" value={form.name} onChange={(e) => set({ name: e.target.value })} className="obs-input" /></label>
                <label className="block"><span className="eyebrow block mb-3">Phone *</span><input data-testid="booking-phone" value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="obs-input" /></label>
              </div>
              <label className="block"><span className="eyebrow block mb-3">Email *</span><input type="email" data-testid="booking-email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="obs-input" /></label>

              <div className="border-t border-ash pt-6 space-y-2 text-sm">
                <p className="eyebrow mb-3">Summary</p>
                <Row k="Style" v={styleMap[form.style_slug] || (form.style_slug ? "Other / Not sure" : "—")} />
                <Row k="Artist" v={form.recommend_artist ? "Recommend for me" : (artists.find((a) => a.slug === form.artist_slug)?.name || "—")} />
                <Row k="Project" v={form.project_type || "—"} />
                <Row k="Placement" v={form.placement || "—"} />
                <Row k="Size" v={form.size || "—"} />
                <Row k="References" v={`${form.reference_images.length} image(s)`} />
              </div>
            </div>
          )}
        </div>

        {/* nav */}
        <div className="mt-8 flex items-center justify-between">
          <button
            data-testid="booking-back"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#C4C4CC] disabled:opacity-30 hover:text-white transition-colors">
            <ArrowLeft size={14} /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              data-testid="booking-next"
              onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Please complete this step first")}
              className={`inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 transition-colors ${canNext() ? "bg-ember text-obsidian hover:bg-white" : "bg-elevated text-[#555562] cursor-not-allowed"}`}>
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button
              data-testid="booking-submit"
              onClick={() => (form.name && form.email && form.phone) ? submit() : toast.error("Name, email and phone are required")}
              disabled={submitting}
              className="inline-flex items-center gap-3 bg-ember text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 hover:bg-white transition-colors disabled:opacity-50">
              {submitting ? "Submitting…" : "Submit Request"} <ArrowRight size={14} />
            </button>
          )}
        </div>
      </section>

      <style>{`.obs-input{width:100%;background:#0d0d11;border:1px solid #282832;color:#F4F4F6;padding:14px 16px;font-size:15px;outline:none;transition:border-color .3s}.obs-input:focus{border-color:#A855F7}`}</style>
    </>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-3">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="obs-input appearance-none cursor-pointer">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-ash/50">
      <span className="text-[#828290] font-mono text-[0.7rem] uppercase tracking-widest">{k}</span>
      <span className="text-white">{v}</span>
    </div>
  );
}
